import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Routes, Route } from 'react-router-dom';

import { AppLayout } from '@/components/layout/AppLayout';
import { AuthProvider } from '@/contexts/AuthContext';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { OnboardingDataLayout } from '@/components/onboarding/OnboardingDataLayout';

import { AdminAuthProvider } from '@/contexts/AdminAuthContext';
import { RequirePlatformAdmin } from '@/components/admin/RequirePlatformAdmin';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { AdminLoginPage } from '@/pages/admin/AdminLoginPage';
import { AdminMetricsPage } from '@/pages/admin/AdminMetricsPage';
import { AdminAcquisitionPage } from '@/pages/admin/AdminAcquisitionPage';
import { AdminSupportPage } from '@/pages/admin/AdminSupportPage';

import { LoginPage } from '@/pages/auth/LoginPage';
import { SignupPage } from '@/pages/auth/SignupPage';
import { ForgotPasswordPage } from '@/pages/auth/ForgotPasswordPage';
import { ResetPasswordPage } from '@/pages/auth/ResetPasswordPage';
import { VerifyEmailPage } from '@/pages/auth/VerifyEmailPage';

import { AcceptInvitePage } from '@/pages/auth/AcceptInvitePage';
import { BotPage } from '@/pages/BotPage';
import { ConversationDetailPage } from '@/pages/ConversationDetailPage';
import { ConversationsPage } from '@/pages/ConversationsPage';
import { LeadDetailPage } from '@/pages/LeadDetailPage';
import { LeadsPage } from '@/pages/LeadsPage';
import { NotificationsPage } from '@/pages/NotificationsPage';
import { NotFoundGate, NotFoundPage } from '@/pages/NotFoundPage';
import { MetaCallbackPage } from '@/pages/MetaCallbackPage';
import { SettingsPage } from '@/pages/SettingsPage';
import { StripeCancelPage } from '@/pages/StripeCancelPage';
import { StripeSuccessPage } from '@/pages/StripeSuccessPage';
import { TeamPage } from '@/pages/TeamPage';
import { TestBotPage } from '@/pages/TestBotPage';

import './styles/globals.css';
import '@/i18n';

const ADMIN_SECRET = import.meta.env.VITE_ADMIN_PATH_SECRET ?? '__ops_dev';

const root = document.getElementById('root');
if (!root) throw new Error('Root element #root not found in index.html');

createRoot(root).render(
  <StrictMode>
    <BrowserRouter>
      {/* Admin plane — completely independent from the tenant AuthProvider.
          Routes are matched before the tenant catch-all. */}
      <AdminAuthProvider>
        <Routes>
          <Route path={`/${ADMIN_SECRET}/login`} element={<AdminLoginPage />} />
          <Route element={<RequirePlatformAdmin />}>
            <Route element={<AdminLayout />}>
              <Route path={`/${ADMIN_SECRET}`} index element={<AdminMetricsPage />} />
              <Route path={`/${ADMIN_SECRET}/acquisition`} element={<AdminAcquisitionPage />} />
              <Route path={`/${ADMIN_SECRET}/support`} element={<AdminSupportPage />} />
            </Route>
          </Route>
        </Routes>
      </AdminAuthProvider>

      <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/accept-invite" element={<AcceptInvitePage />} />

          <Route element={<ProtectedRoute />}>
            <Route element={<OnboardingDataLayout />}>
              {/* Path-exact redirects for legacy routes — sibling routes
                  like /onboarding/meta/callback (F027) remain reachable. */}
              <Route path="/onboarding" element={<Navigate to="/" replace />} />
              <Route path="/onboarding/meta/callback" element={<MetaCallbackPage />} />
              <Route path="/onboarding/stripe/success" element={<StripeSuccessPage />} />
              <Route path="/onboarding/stripe/cancel" element={<StripeCancelPage />} />
              <Route element={<AppLayout />}>
                <Route index element={<BotPage />} />
                <Route path="/bot" element={<Navigate to="/" replace />} />
                <Route path="/test" element={<TestBotPage />} />
                <Route path="/leads" element={<LeadsPage />} />
                <Route path="/leads/:id" element={<LeadDetailPage />} />
                <Route path="/conversations" element={<ConversationsPage />} />
                <Route path="/conversations/:contactId" element={<ConversationDetailPage />} />
                <Route path="/notifications" element={<NotificationsPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/team" element={<TeamPage />} />
              </Route>
            </Route>
          </Route>

          {/* F064 — catch-all 404. Lives OUTSIDE ProtectedRoute so an unknown
              URL is never silently bounced to /login: the gate renders the
              bare 404 (link to /login) when unauthenticated and Outlets into
              the real shell layouts (sidebar/header + NotFoundPage) when
              authenticated. Specific routes above always win over `*`. */}
          <Route element={<NotFoundGate />}>
            <Route element={<OnboardingDataLayout />}>
              <Route element={<AppLayout />}>
                <Route path="*" element={<NotFoundPage />} />
              </Route>
            </Route>
          </Route>
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  </StrictMode>
);
